import Button from "@components/button"
import CustomerSelect from "@components/customer_select"
import TextInput from "@components/text_input"
import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import { useAppContext } from "@contexts/app_context"
import {
  AiAnswersFormData,
  useAiAnswersPageContext
} from "@contexts/main/ai_answers_page_context"
import { useAiHubContext } from "@contexts/main/ai_hub_context"
import { deleteAiMessages } from "@domain/ai_answers"
import FilterListIcon from "@mui/icons-material/FilterList"
import {
  Box,
  IconButton,
  InputLabel,
  Popover,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { useQueryClient } from "@tanstack/react-query"
import { showSuccessToast } from "@utils/toast"
import { PlatformType, RoleTypes } from "__generated__/graphql"
import dayjs from "dayjs"
import "dayjs/locale/tr"
import { useEffect, useState } from "react"
import { RUN_KIND_FILTER_OPTIONS } from "./aiMessage.utils"

enum AiMessageStatus {
  RUNNING = "RUNNING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED"
}

export default function AiAnswersFilterPopover() {
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    filters,
    setSelectedCustomer,
    setFilters,
    translate
  } = useAiAnswersPageContext()

  const { me } = useAppContext()
  const { customerId: hubCustomerId, setCustomerId: setHubCustomerId } =
    useAiHubContext()
  const queryClient = useQueryClient()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isStartDateOpen, setIsStartDateOpen] = useState(false)
  const [isEndDateOpen, setIsEndDateOpen] = useState(false)
  const [isDateFocus, setIsDateFocus] = useState(false)

  const customerId = watch("customerId")
  const startDate = watch("startDate")
  const endDate = watch("endDate")

  // Hub'tan gelen müşteri ile form'u senkronize et (mount + hub değişimi)
  useEffect(() => {
    if (hubCustomerId && hubCustomerId !== customerId) {
      setValue("customerId", hubCustomerId)
      setFilters((prev) => ({ ...prev, customerId: hubCustomerId }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubCustomerId])

  // Form'da müşteri değişince hub'a yaz
  useEffect(() => {
    if ((customerId || "") !== (hubCustomerId || "")) {
      setHubCustomerId(customerId || "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  function normalizeDateRange(data: AiAnswersFormData): AiAnswersFormData {
    const normalized = { ...data }

    if (normalized.startDate && !normalized.endDate) {
      normalized.endDate = dayjs().endOf("day").toDate()
    }

    if (!normalized.startDate && normalized.endDate) {
      normalized.startDate = dayjs(normalized.endDate).startOf("day").toDate()
    }

    if (
      normalized.startDate &&
      normalized.endDate &&
      normalized.startDate > normalized.endDate
    ) {
      const start = dayjs(normalized.endDate).startOf("day").toDate()
      const end = dayjs(normalized.startDate).endOf("day").toDate()
      normalized.startDate = start
      normalized.endDate = end
    }

    return normalized
  }

  function applyFilters(data: AiAnswersFormData) {
    const normalized = normalizeDateRange(data)

    setFilters({
      ...filters,
      ...normalized,
      customerId: normalized.customerId || "",
      channelType: normalized.channelType || undefined,
      status: normalized.status || undefined,
      kind: normalized.kind || undefined
    })
    queryClient.invalidateQueries({ queryKey: ["ai-message-list"] })
    setAnchorEl(null)
  }

  async function deleteAiMessagesByDateRange(data: AiAnswersFormData) {
    if (!data.startDate)
      data.startDate = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
    if (!data.endDate) data.endDate = new Date()
    const response = await deleteAiMessages({
      customerId: data?.customerId,
      start: data.startDate,
      end: data.endDate
    })
    if (response.data) {
      showSuccessToast(
        `${response?.data?.deletedCount} messages deleted. ${response?.data?.deletedSteps} steps deleted.`
      )
      queryClient.invalidateQueries({ queryKey: ["ai-message-list"] })
    }
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Tooltip title={translate("filter")}>
        <IconButton
          disableRipple
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            color: open ? PRIMARY : "inherit",
            ":hover": { color: PRIMARY }
          }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 260, p: 1.5 } } }}
      >
        <Stack gap={1.25}>
          <CustomerSelect
            control={control}
            name="customerId"
            label={translate("customer")}
            onSelect={setSelectedCustomer}
          />
          <TextInput
            control={control}
            name="channelType"
            placeholder={translate("select")}
            type="select"
            label={translate("channel_type")}
            selectOptions={[
              { value: "", label: translate("all") },
              ...Object.values(PlatformType).map((item) => ({
                value: item,
                label: item
              }))
            ]}
          />
          <TextInput
            control={control}
            name="status"
            placeholder={translate("select")}
            type="select"
            label={translate("message_status")}
            selectOptions={[
              { value: "", label: translate("all") },
              ...Object.values(AiMessageStatus).map((item) => ({
                value: item,
                label: translate(item)
              }))
            ]}
          />
          <TextInput
            control={control}
            name="kind"
            placeholder={translate("select")}
            type="select"
            label={translate("ai_logs_run_kind")}
            selectOptions={[
              { value: "", label: translate("all") },
              ...RUN_KIND_FILTER_OPTIONS.map((option) => ({
                value: option.value,
                label: translate(option.labelKey)
              }))
            ]}
          />

          <Stack>
            <InputLabel
              sx={{
                color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
                alignItems: "center"
              }}
            >
              <Typography>{translate("start_date")}</Typography>
            </InputLabel>
            <Box
              sx={{
                border: "1px solid #DDDDDD",
                borderRadius: 1,
                ":hover": { border: "1px solid #000000" }
              }}
            >
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="tr"
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  value={startDate ? dayjs(startDate) : null}
                  sx={{
                    "& input": { width: "100%" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { border: "none" }
                    },
                    width: "100%"
                  }}
                  disableOpenPicker
                  open={isStartDateOpen}
                  onClose={() => setIsStartDateOpen(false)}
                  slotProps={{
                    textField: {
                      size: "small",
                      onClick: () => setIsStartDateOpen(true),
                      onFocus: () => setIsDateFocus(true),
                      onBlur: () => setIsDateFocus(false)
                    },
                    field: {
                      clearable: true,
                      onClear: () => setValue("startDate", undefined)
                    }
                  }}
                  onChange={(date) => {
                    setValue(
                      "startDate",
                      date ? date.startOf("day").toDate() : undefined
                    )
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Stack>

          <Stack>
            <InputLabel
              sx={{
                color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
                alignItems: "center"
              }}
            >
              <Typography>{translate("end_date")}</Typography>
            </InputLabel>
            <Box
              sx={{
                border: "1px solid #DDDDDD",
                borderRadius: 1,
                ":hover": { border: "1px solid #000000" }
              }}
            >
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="tr"
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  value={endDate ? dayjs(endDate) : null}
                  sx={{
                    "& input": { width: "100%" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { border: "none" }
                    },
                    width: "100%"
                  }}
                  disableOpenPicker
                  open={isEndDateOpen}
                  onClose={() => setIsEndDateOpen(false)}
                  slotProps={{
                    textField: {
                      size: "small",
                      onClick: () => setIsEndDateOpen(true),
                      onFocus: () => setIsDateFocus(true),
                      onBlur: () => setIsDateFocus(false)
                    },
                    field: {
                      clearable: true,
                      onClear: () => setValue("endDate", undefined)
                    }
                  }}
                  onChange={(date) => {
                    setValue(
                      "endDate",
                      date ? date.endOf("day").toDate() : undefined
                    )
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Stack>

          <Button onClick={handleSubmit(applyFilters)}>
            {translate("filter")}
          </Button>

          <Button
            color="error"
            disabled={me?.role === RoleTypes.User}
            onClick={handleSubmit((data) => {
              const msg = "Bu işlem geri alınamaz. Silmek istiyor musun?"
              if (window.confirm(msg)) {
                void deleteAiMessagesByDateRange(data)
                setAnchorEl(null)
              }
            })}
          >
            {translate("delete_date_range")}
          </Button>
        </Stack>
      </Popover>
    </>
  )
}
